/**
 * Bootstraps the streaming-adapter selector.
 *
 * On mount (and when the company-API client becomes available), pings the
 * backend's adapter directory. If at least one adapter is registered, we
 * flip the wrapper into "streaming available" state and pre-select the
 * default adapter. Otherwise we stay in batch mode silently — non-fatal,
 * the legacy panel just hides the streaming controls.
 *
 * Side effect: also flips wrapper mode to "streaming" the first time
 * adapters are seen, matching the behaviour before extraction (companies
 * with streaming default to the streaming UI).
 */
import { useEffect } from "react"
import type { CompanyApi } from "../../../hooks/useCompanyApi"
import { fetchStreamingAdapters } from "../../../lib/api"
import type { TRecordingMode } from "../interfaces"

interface IAdapter {
  name: string
  label: string
}

interface IProps {
  company: CompanyApi | null
  setStreamingAvailable: (available: boolean) => void
  setStreamingAdapterList: (adapters: IAdapter[]) => void
  setDefaultAdapterName: (name: string) => void
  setMode: (mode: TRecordingMode) => void
}

export function useStreamingAdapters(props: IProps): void {
  const { company, setStreamingAvailable, setStreamingAdapterList, setDefaultAdapterName, setMode } = props

  useEffect(() => {
    if (!company) return
    fetchStreamingAdapters(company)
      .then((res) => {
        const result = res as { adapters: IAdapter[]; defaultAdapter: string }
        if (result.adapters.length > 0) {
          setStreamingAvailable(true)
          setStreamingAdapterList(result.adapters)
          setDefaultAdapterName(result.defaultAdapter)
          setMode("streaming")
        }
      })
      .catch(() => {
        /* streaming not available, stay in batch mode */
      })
  }, [company, setStreamingAvailable, setStreamingAdapterList, setDefaultAdapterName, setMode])
}
